/**
 * PLEKIFY HOMEPAGE — COMBINED JAVASCRIPT
 * Master JS for all homepage sections and interactive components
 *
 * @version 1.0.0
 * @date 2026-03-31
 *
 * Components:
 * 1. Citation System
 * 2. Stat Counter Animation
 * 3. Stream Switcher
 * 4. Pipeline Flow (if present)
 * 5. Selection Funnel (if present)
 * 6. Friction Comparison (if present)
 * 7. Navigation Pill (scroll-linked)
 * 8. Intersection Observer utilities
 */

(function() {
  'use strict';

  // ============================================
  // UTILITIES
  // ============================================

  /**
   * Debounce function for scroll/resize handlers
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Throttle function for animation frames
   */
  function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Format numbers with locale-aware separators
   */
  function formatNumber(num, locale = 'en-ZA') {
    return new Intl.NumberFormat(locale).format(num);
  }

  /**
   * Parse numeric value from string (handles commas, currency symbols)
   */
  function parseNumericValue(str) {
    if (typeof str === 'number') return str;
    const cleaned = str.replace(/[^0-9.-]/g, '');
    return parseFloat(cleaned) || 0;
  }


  // ============================================
  // 1. STAT COUNTER ANIMATION
  // ============================================

  const StatCounter = {
    observer: null,

    init: function() {
      const stats = document.querySelectorAll('[data-stat-counter]');
      if (!stats.length) return;

      // Use Intersection Observer for scroll-triggered animation
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !entry.target.classList.contains('is-complete')) {
            this.animateCounter(entry.target);
          }
        });
      }, {
        threshold: 0.5,
        rootMargin: '0px 0px -50px 0px'
      });

      stats.forEach(stat => this.observer.observe(stat));
    },

    animateCounter: function(element) {
      const numberEl = element.querySelector('.plk-stat__number');
      if (!numberEl) return;

      const targetValue = parseNumericValue(element.dataset.statCounter);
      const duration = parseInt(element.dataset.statDuration) || 2000;
      const prefix = element.dataset.statPrefix || '';
      const suffix = element.dataset.statSuffix || '';

      element.classList.add('is-counting');

      const startTime = performance.now();
      const startValue = 0;

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const currentValue = Math.round(startValue + (targetValue - startValue) * easedProgress);

        numberEl.textContent = prefix + formatNumber(currentValue) + suffix;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          element.classList.remove('is-counting');
          element.classList.add('is-complete');
        }
      };

      // Respect reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        numberEl.textContent = prefix + formatNumber(targetValue) + suffix;
        element.classList.add('is-complete');
      } else {
        requestAnimationFrame(animate);
      }
    }
  };


  // ============================================
  // 3. STREAM SWITCHER
  // ============================================

  const StreamSwitcher = {
    currentStream: 'itinerary',

    init: function() {
      const switcher = document.querySelector('[data-component="stream-switcher"]');
      if (!switcher) return;

      const config = this.getConfig(switcher);

      // Handle card clicks
      const cards = switcher.querySelectorAll('.plk-switcher__card');
      cards.forEach(card => {
        card.addEventListener('click', (e) => {
          const stream = card.dataset.stream;
          if (stream) {
            this.switchStream(stream, config);
          }
        });
      });

      // Update current stream indicator
      this.updateCurrentIndicator();
    },

    getConfig: function(switcher) {
      const configEl = switcher.querySelector('[data-plekify-switcher-config]');
      if (configEl) {
        try {
          return JSON.parse(configEl.textContent);
        } catch (e) {
          console.warn('Failed to parse switcher config:', e);
        }
      }
      return {};
    },

    switchStream: function(stream, config) {
      this.currentStream = stream;

      // Update URL without reload
      const url = stream === 'itinerary' ? config.itineraryUrl : config.accommodationUrl;
      if (url && url !== '#' + stream) {
        history.pushState({ stream }, '', url);
      }

      // Scroll to stream content
      const targetSection = document.querySelector(`[data-stream="${stream}"]`);
      if (targetSection) {
        targetSection.scrollIntoView({
          behavior: config.scrollBehavior || 'smooth'
        });
      }

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('plekify:streamChange', {
        detail: { stream }
      }));

      this.updateCurrentIndicator();
    },

    updateCurrentIndicator: function() {
      const indicator = document.querySelector('.plk-switcher__current-stream');
      if (indicator) {
        indicator.textContent = this.currentStream === 'itinerary' ?
          'Itinerary Commerce' : 'Accommodation Booking';
      }
    }
  };


  // ============================================
  // 4. NAVIGATION PILL (Scroll-Linked)
  // ============================================

  const NavPill = {
    nav: null,
    heroHeight: 0,
    overlap: 100,
    stickPoint: 20,
    seamPosition: 0,
    isStuck: false,
    hasVideo: false,

    init: function() {
      // Support both class names for backwards compatibility
      this.nav = document.querySelector('.plk-nav') || document.querySelector('.plk-nav-pill');
      if (!this.nav) return;

      // Check if we have a video layer (B2C) or not (B2B)
      const videoLayer = document.querySelector('#plekify-video-layer') || document.querySelector('.plk-hero__video-stage video');
      this.hasVideo = !!videoLayer;

      this.calculatePositions();
      this.bindEvents();
      this.updatePosition();

      // Expose global functions for nav onclick handlers
      this.exposeGlobalFunctions();
    },

    calculatePositions: function() {
      if (this.hasVideo) {
        // B2C with video: position nav at video/content seam
        const hero = document.querySelector('.plk-hero__video-stage');
        if (hero) {
          this.heroHeight = hero.offsetHeight;
        } else {
          this.heroHeight = window.innerHeight * 0.55;
        }
        const navHeight = this.nav.offsetHeight;
        this.seamPosition = this.heroHeight - this.overlap - (navHeight / 2);
      } else {
        // B2B without video: nav sticks at top immediately
        this.seamPosition = 0;
      }
    },

    bindEvents: function() {
      window.addEventListener('scroll', throttle(() => this.updatePosition(), 16), { passive: true });
      window.addEventListener('resize', debounce(() => {
        this.calculatePositions();
        this.updatePosition();
      }, 100));
    },

    updatePosition: function() {
      const scrollY = window.scrollY;

      if (!this.hasVideo) {
        // B2B: Always stuck at top
        this.nav.style.top = this.stickPoint + 'px';
        this.nav.classList.add('plk-nav--scrolled');
        this.isStuck = true;
        return;
      }

      // B2C with video: scroll-linked positioning
      if (scrollY > this.seamPosition - this.stickPoint) {
        if (!this.isStuck) {
          this.nav.style.top = this.stickPoint + 'px';
          this.nav.classList.add('plk-nav--scrolled');
          this.isStuck = true;
        }
      } else {
        this.nav.style.top = (this.seamPosition - scrollY) + 'px';
        this.nav.classList.remove('plk-nav--scrolled');
        this.isStuck = false;
      }
    },

    exposeGlobalFunctions: function() {
      // Global function for nav stream toggle buttons
      window.plkJumpToStream = function(stream) {
        // Update body data attribute
        document.body.setAttribute('data-plekify-stream', stream);

        // Update nav toggle active states
        document.querySelectorAll('[data-plk-nav-stream]').forEach(btn => {
          const isActive = btn.dataset.plkNavStream === stream;
          btn.classList.toggle('plk-nav--active', isActive);
          btn.setAttribute('aria-checked', String(isActive));
        });

        // Scroll to stream anchor if it exists
        const anchor = document.querySelector('#' + stream) || document.querySelector('[data-stream="' + stream + '"]');
        if (anchor) {
          anchor.scrollIntoView({ behavior: 'smooth' });
        }

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('plekify:streamChange', { detail: { stream } }));
      };

      // Global function to return to landing state
      window.plkReturnToLanding = function() {
        document.body.setAttribute('data-plekify-stream', 'landing');

        // Clear nav toggle active states
        document.querySelectorAll('[data-plk-nav-stream]').forEach(btn => {
          btn.classList.remove('plk-nav--active');
          btn.setAttribute('aria-checked', 'false');
        });

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };

      // Initialize body stream state
      if (!document.body.hasAttribute('data-plekify-stream')) {
        document.body.setAttribute('data-plekify-stream', 'landing');
      }
    }
  };


  // ============================================
  // 5. INTERSECTION OBSERVER UTILITIES
  // ============================================

  const ScrollReveal = {
    init: function() {
      const elements = document.querySelectorAll('[data-reveal]');
      if (!elements.length) return;

      // Respect reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        elements.forEach(el => el.classList.add('is-revealed'));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      elements.forEach(el => observer.observe(el));
    }
  };


  // ============================================
  // 6. PIPELINE FLOW COMPONENT
  // ============================================

  const PipelineFlow = {
    init: function() {
      const pipelines = document.querySelectorAll('[data-component="pipeline-flow"]');
      if (!pipelines.length) return;

      pipelines.forEach(pipeline => {
        const stages = pipeline.querySelectorAll('.plk-pipeline__stage');

        stages.forEach((stage, index) => {
          stage.addEventListener('click', () => {
            this.toggleStage(stage, stages);
          });

          stage.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.toggleStage(stage, stages);
            }
          });
        });
      });
    },

    toggleStage: function(stage, allStages) {
      const isExpanded = stage.getAttribute('aria-expanded') === 'true';

      // Close all others
      allStages.forEach(s => {
        s.setAttribute('aria-expanded', 'false');
        s.classList.remove('is-expanded');
      });

      // Toggle current
      if (!isExpanded) {
        stage.setAttribute('aria-expanded', 'true');
        stage.classList.add('is-expanded');
      }
    }
  };


  // ============================================
  // 7. SELECTION FUNNEL COMPONENT
  // ============================================

  const SelectionFunnel = {
    init: function() {
      const funnels = document.querySelectorAll('[data-component="selection-funnel"]');
      if (!funnels.length) return;

      funnels.forEach(funnel => {
        const stages = funnel.querySelectorAll('.plk-funnel__stage');

        // Stagger animation on scroll
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animateStages(stages);
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.3 });

        observer.observe(funnel);
      });
    },

    animateStages: function(stages) {
      stages.forEach((stage, index) => {
        setTimeout(() => {
          stage.classList.add('is-visible');
        }, index * 150);
      });
    }
  };


  // ============================================
  // 8. FRICTION COMPARISON COMPONENT
  // ============================================

  const FrictionComparison = {
    init: function() {
      const components = document.querySelectorAll('[data-component="friction-comparison"]');
      if (!components.length) return;

      components.forEach(component => {
        const tabs = component.querySelectorAll('[role="tab"]');
        const panels = component.querySelectorAll('[role="tabpanel"]');

        tabs.forEach(tab => {
          tab.addEventListener('click', () => {
            this.switchTab(tab, tabs, panels);
          });

          tab.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.switchTab(tab, tabs, panels);
            }
          });
        });
      });
    },

    switchTab: function(selectedTab, allTabs, allPanels) {
      const targetId = selectedTab.getAttribute('aria-controls');

      // Update tabs
      allTabs.forEach(tab => {
        tab.setAttribute('aria-selected', 'false');
        tab.classList.remove('is-active');
      });
      selectedTab.setAttribute('aria-selected', 'true');
      selectedTab.classList.add('is-active');

      // Update panels
      allPanels.forEach(panel => {
        panel.hidden = panel.id !== targetId;
      });
    }
  };


  // ============================================
  // 9. EVENT MEASUREMENT
  // ============================================

  const EventMeasurement = {
    init: function() {
      var events = [
        'hero_primary_cta_click',
        'hero_secondary_cta_click',
        'pricing_deposit_click',
        'pricing_proposal_click',
        'demo_store_click',
        'friction_methodology_click',
        'proposal_form_submit'
      ];

      events.forEach(function(eventName) {
        document.addEventListener(eventName === 'proposal_form_submit' ? 'submit' : 'click', function(e) {
          var target = e.target.closest('[data-event="' + eventName + '"]');
          if (!target) return;

          var payload = {
            event: eventName,
            element: target.tagName.toLowerCase(),
            href: target.href || null,
            label: target.textContent ? target.textContent.trim().substring(0, 100) : null,
            demo_store: target.dataset.demoStore || null,
            timestamp: new Date().toISOString()
          };

          // GA4 dataLayer
          if (window.dataLayer) {
            window.dataLayer.push(payload);
          }

          // Shopify Customer Events
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish(eventName, payload);
          }
        }, true);
      });
    }
  };

  // ============================================
  // INITIALIZATION
  // ============================================

  function initPlekifyHomepage() {
    // Initialize all components
    StatCounter.init();
    StreamSwitcher.init();
    NavPill.init();
    ScrollReveal.init();
    PipelineFlow.init();
    SelectionFunnel.init();
    FrictionComparison.init();
    EventMeasurement.init();

    console.log('Plekify Homepage initialized');
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPlekifyHomepage);
  } else {
    initPlekifyHomepage();
  }

  // Expose for external use if needed
  window.PlekifyHomepage = {
    StatCounter: StatCounter,
    StreamSwitcher: StreamSwitcher,
    NavPill: NavPill,
    ScrollReveal: ScrollReveal,
    PipelineFlow: PipelineFlow,
    SelectionFunnel: SelectionFunnel,
    FrictionComparison: FrictionComparison,
    EventMeasurement: EventMeasurement
  };

})();

/* ===== RUNTIME FIXES 2026-05-31 ===== */
(function() {
  'use strict';

  /* 1. Force travel search visible */
  function showTravelSearch() {
    document.querySelectorAll('.shopify-section').forEach(function(s) {
      if (s.classList.contains('travel-search-frame-section') || s.classList.contains('travel-search-frame-section-v2')) {
        s.style.cssText = 'display:block!important;visibility:visible!important;opacity:1!important;';
      }
    });
  }
  showTravelSearch();
  setTimeout(showTravelSearch, 100);
  setTimeout(showTravelSearch, 500);
  setTimeout(showTravelSearch, 1500);

  /* 2. Replace old account text with white icon */
  function fixAccountIcon() {
    var nav = document.querySelector('.microsite-hero__account');
    if (!nav) return;
    var old = nav.querySelectorAll('.account-popover, .account-drawer');
    if (old.length === 0) return;
    old.forEach(function(el) { el.remove(); });

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'microsite-account-icon';
    btn.setAttribute('aria-label', 'Account');
    btn.style.cssText = 'background:none;border:none;color:#fff;cursor:pointer;padding:6px;display:flex;align-items:center;justify-content:center;transition:opacity 0.2s;line-height:1;';
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg>';

    var drawerId = 'microsite-account-drawer-runtime';
    var existing = document.getElementById(drawerId);
    if (existing) existing.remove();

    var drawer = document.createElement('div');
    drawer.id = drawerId;
    drawer.style.cssText = 'position:fixed;inset:0;z-index:10001;pointer-events:none;visibility:hidden;opacity:0;transition:opacity 0.3s,visibility 0.3s;';
    drawer.innerHTML = '<div style="position:absolute;inset:0;background:rgba(0,0,0,0.35);backdrop-filter:blur(2px);"></div>' +
      '<div style="position:absolute;top:0;right:0;width:420px;max-width:90vw;height:100%;background:#fff;color:#1a1a1a;transform:translateX(100%);transition:transform 0.4s cubic-bezier(0.25,0.1,0.25,1);display:flex;flex-direction:column;box-shadow:-4px 0 24px rgba(0,0,0,0.1);">' +
      '<button type="button" aria-label="Close" style="position:absolute;top:20px;right:20px;background:none;border:none;cursor:pointer;padding:8px;color:#1a1a1a;line-height:1;z-index:2;">' +
      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4l12 12M16 4L4 16"/></svg></button>' +
      '<div style="padding:80px 44px 44px;flex:1;display:flex;flex-direction:column;">' +
      '<p style="font-size:10px;font-weight:500;letter-spacing:0.18em;text-transform:uppercase;color:#999;margin:0 0 10px;">Simbavati</p>' +
      '<h2 style="font-size:clamp(1.5rem,3vw,2rem);font-weight:400;margin:0 0 18px;color:#1a1a1a;line-height:1.15;">Account</h2>' +
      '<p style="font-size:15px;line-height:1.6;color:#555;margin:0 0 44px;">Log into your Simbavati property management system on Shopify.</p>' +
      '<div style="margin-top:auto;">' +
      '<a href="/account/login" style="display:block;width:100%;padding:16px;background:#1a1a1a;color:#fff;text-align:center;text-decoration:none;font-size:12px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:12px;border:1px solid #1a1a1a;">Log in</a>' +
      '<a href="/account/register" style="display:block;width:100%;padding:16px;background:transparent;color:#1a1a1a;text-align:center;text-decoration:none;font-size:12px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;border:1px solid #1a1a1a;">Create account</a>' +
      '</div></div></div>';
    document.body.appendChild(drawer);

    var backdrop = drawer.children[0];
    var panel = drawer.children[1];
    var closeBtn = panel.querySelector('button');

    function open() {
      drawer.style.pointerEvents = 'auto';
      drawer.style.visibility = 'visible';
      drawer.style.opacity = '1';
      panel.style.transform = 'translateX(0)';
    }
    function close() {
      drawer.style.pointerEvents = 'none';
      drawer.style.visibility = 'hidden';
      drawer.style.opacity = '0';
      panel.style.transform = 'translateX(100%)';
    }

    btn.addEventListener('click', open);
    backdrop.addEventListener('click', close);
    closeBtn.addEventListener('click', close);

    nav.innerHTML = '';
    nav.appendChild(btn);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixAccountIcon);
  } else {
    fixAccountIcon();
  }
  setTimeout(fixAccountIcon, 500);
  setTimeout(fixAccountIcon, 1500);

  /* 3. Calendar upward pop patch */
  function patchCalendar() {
    if (!window.TravelSearchComponent) {
      setTimeout(patchCalendar, 300);
      return;
    }
    var proto = window.TravelSearchComponent.prototype;
    if (proto._calendarPatchedRuntime) return;
    proto._calendarPatchedRuntime = true;

    proto.scrollToAccommodateCalendar = function() {};
    proto.scrollToTopOnMobile = function() {};

    var orig = proto.updateDropdownPosition;
    proto.updateDropdownPosition = function(dropdown, trigger) {
      if (dropdown && dropdown.classList.contains('calendar-container')) {
        var triggerRect = trigger.getBoundingClientRect();
        var dropdownHeight = dropdown.scrollHeight || dropdown.getBoundingClientRect().height || 520;
        var top = triggerRect.top - dropdownHeight - 12;
        if (top < 8) top = 8;
        dropdown.style.position = 'fixed';
        dropdown.style.top = top + 'px';
        dropdown.style.left = '0px';
        dropdown.style.width = window.innerWidth + 'px';
        dropdown.style.right = 'auto';
        dropdown.style.bottom = 'auto';
        dropdown.style.maxHeight = 'none';
        dropdown.style.height = 'auto';
        dropdown.style.overflowY = 'visible';
        return;
      }
      return orig.call(this, dropdown, trigger);
    };

    var origShow = proto.showCalendar;
    proto.showCalendar = function() {
      var isAlreadyShowing = this.elements.calendarContainer && this.elements.calendarContainer.classList.contains('show');
      if (!isAlreadyShowing) {
        this.renderCalendar();
        if (this.elements.calendarContainer) {
          this.elements.calendarContainer.style.maxHeight = 'none';
          this.elements.calendarContainer.style.height = 'auto';
          this.elements.calendarContainer.style.overflow = 'visible';
        }
        if (this.elements.calendarContainer) this.elements.calendarContainer.classList.add('show');
        this.moveToPortal(this.elements.calendarContainer, this.elements.checkInInput);
      }
    };
  }
  /* ═══════════════════════════════════════════════════════════
     TRAVEL SEARCH CALENDAR — Scroll section to top on open
     Prevents calendar from opening beneath the fold
     ═══════════════════════════════════════════════════════════ */
  function bindTravelSearchScroll() {
    var sectionEl = document.querySelector('.shopify-section.travel-search-frame-section-v2, .shopify-section.travel-search-frame-section-v3');
    if (!sectionEl) return;
    var inputs = sectionEl.querySelectorAll('.travel-search-main-wrapper .checkin-input, .travel-search-main-wrapper .checkout-input, .travel-search-main-wrapper .date-input-group');
    inputs.forEach(function(el) {
      if (el.dataset.plkScrollBound === 'true') return;
      el.dataset.plkScrollBound = 'true';
      el.addEventListener('click', function(ev) {
        var headerOffset = 80;
        var targetTop = sectionEl.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchCalendar);
    document.addEventListener('DOMContentLoaded', bindTravelSearchScroll);
  } else {
    patchCalendar();
    bindTravelSearchScroll();
  }
  setTimeout(patchCalendar, 500);
  setTimeout(patchCalendar, 1500);
  setTimeout(bindTravelSearchScroll, 600);
  setTimeout(bindTravelSearchScroll, 1600);
})();
